// LogTypes.ts or a similar location

// Define the possible object types as a string literal union type
export type LogObjectType =
    | 'appointment'
    | 'appointmentStep'
    | 'treatment'
    | 'treatmentVariant'
    | 'treatmentStep'
    | 'customer'
    | 'practitionerCategory'
    | 'roomCategory'
    | 'equipmentCategory'
    | 'practitioner'
    | 'room'
    | 'equipment'
    | 'payment'
    | 'billingDraft';

// Define the structure for each included related object based on the 'select' clauses
// Note: Adjust types (e.g., string vs number for ID) and nullability based on your *actual* schema
// Using 'unknown' or 'any' temporarily if unsure, but specific types are better.

interface LogRelationBase {
    id: string | number; // Use number if the related ID is Int
}

interface LogAppointment extends LogRelationBase {
    id: string;
    originalTreatmentVariants: {
        treatmentVariant: {
            name: string;
        }
    }[];
    steps: {
        dateFrom: string;
        dateUntil: string;
        dateFromLocal: PrismaJson.SerializedLocalDatetime | null;
        dateUntilLocal: PrismaJson.SerializedLocalDatetime | null;
    }[];
}
interface LogAppointmentStep extends LogRelationBase {
    id: string;
}
interface LogTreatment extends LogRelationBase {
    id: string;
    name: string | null;
}
interface LogTreatmentVariant extends LogRelationBase {
    id: string;
    name: string | null;
}
interface LogTreatmentStep extends LogRelationBase {
    id: string;
    name: string | null;
}
interface LogCustomer extends LogRelationBase {
    id: number;
    firstName: string | null; // Use firstName
    lastName: string | null;  // Use lastName
    email: string | null;
}
interface LogPractitionerCategory extends LogRelationBase {
    id: number;
    name: string; // Assuming name is not null
}
interface LogRoomCategory extends LogRelationBase {
    id: number;
    name: string;
}
interface LogEquipmentCategory extends LogRelationBase {
    id: number;
    name: string;
}
interface LogPractitioner extends LogRelationBase {
    id: number;
    firstName: string | null; // Use firstName
    lastName: string | null;  // Use lastName
    // Remove name: string | null; if it's not selected/needed
}
interface LogRoom extends LogRelationBase {
    id: number;
    name: string | null;
}
interface LogEquipment extends LogRelationBase {
    id: number;
    name: string | null;
}
interface LogPayment extends LogRelationBase {
    id: string;
    paidAmountCents: number;
    billingDraft: { billingDraftNumber: string } | null;
}
interface LogBillingDraft extends LogRelationBase {
    id: string;
    billingDraftNumber: string;
}


// Define the main LogEntry type
export type LogEntry = {
    id: string;
    createdAt: Date | string; // Keep this flexible for client-side parsing
    userName: string | null;
    actionExplanation: string | null;

    // Anomalies that were detected and overridden while performing the action
    errors: string[];

    // Foreign keys (keep as is)
    appointmentId: string | null;
    appointmentStepId: string | null;
    treatmentId: string | null;
    treatmentVariantId: string | null;
    treatmentStepId: string | null;
    customerId: number | null;
    practitionerCategoryId: number | null;
    roomCategoryId: number | null;
    equipmentCategoryId: number | null;
    practitionerId: number | null;
    roomId: number | null;
    equipmentId: number | null;
    paymentId: string | null;
    billingDraftId: string | null;

    // Included relation data (These should now match the interfaces above)
    appointment: LogAppointment | null;
    appointmentStep: LogAppointmentStep | null;
    treatment: LogTreatment | null;
    treatmentVariant: LogTreatmentVariant | null;
    treatmentStep: LogTreatmentStep | null;
    customer: LogCustomer | null;
    practitionerCategory: LogPractitionerCategory | null;
    roomCategory: LogRoomCategory | null;
    equipmentCategory: LogEquipmentCategory | null;
    practitioner: LogPractitioner | null;
    room: LogRoom | null;
    equipment: LogEquipment | null;
    payment: LogPayment | null;
    billingDraft: LogBillingDraft | null;
};

export type LogEntryData = LogAppointment
    | LogAppointmentStep
    | LogTreatment
    | LogTreatmentVariant
    | LogTreatmentStep
    | LogCustomer
    | LogPractitionerCategory
    | LogRoomCategory
    | LogEquipmentCategory
    | LogPractitioner
    | LogRoom
    | LogEquipment
    | LogPayment
    | LogBillingDraft

// Define the expected structure of the API response from getLogs
export type GetLogsApiResponse = {
    logs: LogEntry[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
};

// Enum-like object for LogObjectTypes for easy iteration/mapping if needed
// Matches the z.enum definition on the server
export const LogObjectTypesEnum = {
    APPOINTMENT: 'appointment',
    APPOINTMENT_STEP: 'appointmentStep',
    TREATMENT: 'treatment',
    TREATMENT_VARIANT: 'treatmentVariant',
    TREATMENT_STEP: 'treatmentStep',
    CUSTOMER: 'customer',
    PRACTITIONER_CATEGORY: 'practitionerCategory',
    ROOM_CATEGORY: 'roomCategory',
    EQUIPMENT_CATEGORY: 'equipmentCategory',
    PRACTITIONER: 'practitioner',
    ROOM: 'room',
    EQUIPMENT: 'equipment',
    PAYMENT: 'payment',
    BILLING_DRAFT: 'billingDraft',
} as const; // Use 'as const' for stricter typing

// Generate the array of values from the enum-like object
export const logObjectTypeValues = Object.values(LogObjectTypesEnum);